import React, { useEffect, useState, useMemo } from 'react'
import { useController } from 'react-hook-form'
import style from 'jss-material-ui'
import { useTranslate, Button } from 'react-admin'
import debounce from 'lodash.debounce';

import {
    CircularProgress,
    Grid,
    useMediaQuery,
    useTheme,
    TextField,
    Autocomplete,
} from '@mui/material'

import { MapContainer, TileLayer } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import MapDraw from './mapDraw'
import { autocompleteInputCommonStyle } from '../../themes/commonStyles'
import { reverseGeocode } from '../../rest/restClient'
import StandardDialog from '../../ui/dialog/StandardDialog'



const fetchGeoapifySuggestions = async (query) => {
    const apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY
    console.log("<query response : ", query)
    const url = `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(query)}&limit=5&apiKey=${apiKey}`;
  
    const response = await fetch(url);
    console.log("api response : ", response)
    const data = await response.json();
  
    return data.features.map((feature) => ({
      label: feature.properties.formatted,
      lat: feature.geometry.coordinates[1],
      lon: feature.geometry.coordinates[0],
    }));
  };

 



const SMap = style(MapContainer)({
    root: {
        margin: 0,
        display: 'flex',
        flex: '1',
        flexDirection: 'row',
        height: 350,
        width: 350,
    },
})


const getZoomFromRadius = (radius) => {
    let zoom = 9

    if (radius < 100) {
        zoom = 18
    } else if (radius < 250) {
        zoom = 16
    } else if (radius < 1000) {
        zoom = 14
    } else if (radius < 1500) {
        zoom = 13
    } else if (radius < 3000) {
        zoom = 12
    } else if (radius <= 7500) {
        zoom = 11
    } else if (radius <= 10000) {
        zoom = 10
    } else if (radius <= 20000) {
        zoom = 9
    } else if (radius <= 50000) {
        zoom = 8
    } else if (radius <= 80000) {
        zoom = 7
    } else if (radius <= 100000) {
        zoom = 6
    } else if (radius >= 100000) {
        zoom = 5
    }
    return zoom
}

const BodyComponent = ({ record, setOpenDialog }) => {

    const translate = useTranslate();
    const theme = useTheme()
    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'))
    const [loading, setLoading] = useState(false);
    const { field: radiusField } = useController({ name: "geofence_radius", defaultValue: 200 });
    const { field: longitudeField } = useController({ name: "geofence_longitude", defaultValue: 2.2950 });
    const { field: latitudeField } = useController({ name: "geofence_latitude", defaultValue: 48.8738 });
    const { field: formattedAddressField } = useController({ name: "geofence_formatted_address", defaultValue: "Place Charles de Gaulle, 75008 Paris, France" });
    const [center, setCenter] = useState({ lat: record?.geofence_latitude ? record.geofence_latitude : 48.8738, lng: record?.geofence_longitude ? record.geofence_longitude : 2.2950 });
    const [place, setPlace] = useState(record?.geofence_formatted_address ? record.geofence_formatted_address : "Place Charles de Gaulle, 75008 Paris, France");
    const [places, setPlaces] = useState([record?.geofence_formatted_address ? record.geofence_formatted_address : "Place Charles de Gaulle, 75008 Paris, France"]);
    const [radius, setRadius] = useState(record?.geofence_radius ? record.geofence_radius : 200);
    const [zoom, setZoom] = useState(getZoomFromRadius(record?.geofence_radius ? record.geofence_radius : 200));


    const debouncedFetch = useMemo(() =>
        debounce(async (query) => {
            setLoading(true);
            try {
                const response = await fetchGeoapifySuggestions(query);
                setPlaces(response);
            } catch (error) {
                console.error("Error fetching places:", error);
            } finally {
                setLoading(false);
            }
        }, 2000)  // waits 1000ms after last keystroke
    , []);

    const handleInputChange = (event, newInputValue) => {
        if (event === null) return;
        setPlace(newInputValue);
        if (typeof newInputValue !== "string") return;
        setPlace(newInputValue);
        if (newInputValue.length > 4) {
            debouncedFetch(newInputValue);
        } else {
            setPlaces([]);
        }
    };



    const handleGeofenceSubmit = () => {
        if (center && center.lat) latitudeField.onChange(center.lat)
        if (center && center.lng) longitudeField.onChange(center.lng)
        if (radius) radiusField.onChange(radius)
        if (place) formattedAddressField.onChange(place)
        setOpenDialog(false)
    };





    const onPlaceSelected = (_event, suggestion) => {
        if (suggestion && suggestion.lat && suggestion.lon) {
            changeCenter({
                lat: suggestion.lat,
                lng: suggestion.lon,
                address: suggestion.label,
            }, 100);
        }

    };


    const reverseGeocoding = (latitude, longitude) => {
        try {

            reverseGeocode(latitude, longitude)
                .then(function (response) {
                    return response.json;
                }).then(function (data) {
                    setPlace(data.formatted_address)
                    setPlaces([data.formatted_address])
                });

        } catch (ex) {

        }
    }




    const changeCenter = ({ lat, lng, address }, radius) => {

        setCenter({ lat: lat, lng: lng })
        setRadius(radius)
        setPlace(address)
        setZoom(getZoomFromRadius(radius))
    };


    const onEdited = e => {
        const layer = e.layer
        const lat = layer._latlng.lat
        const lng = layer._latlng.lng
        changeCenter({ lat: lat, lng: lng, address: '' }, Math.round(layer._mRadius / 100) * 100)
        reverseGeocoding(lat, lng)

    };




    return (
        <Grid container sx={{ width: '100%' }}>

            <Grid item xs={12} sx={{ marginTop: isSmall ? "10px" : "0px" }}>
                <Autocomplete
                    options={places}
                    loading={loading}
                    isOptionEqualToValue={(option, value) => option.label === value.label}
                    getOptionLabel={(option) => option.label || ''}  // Customize according to your API response
                    onChange={onPlaceSelected}  // Trigger when an option is selected
                    inputValue={place}
                    onInputChange={handleInputChange}  // Handle typing to trigger API fetch
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Enter Address"
                            variant="outlined"
                            fullWidth
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    sx={autocompleteInputCommonStyle({ theme, isSmall, width: isSmall ? "250px" : "400px" })}
                />

            </Grid>

            <Grid item xs={12} sx={{ marginTop: "10px" }}>
                <SMap
                    key={(center && zoom && radius ? center.lat + center.lng + zoom + radius : 0).toString()}
                    center={center}
                    zoom={zoom}
                    style={{ width: '100%', borderRadius: "12px" }}>
                    <TileLayer
                        attribution="&amp;copy <a href=&quot;http://osm.org/copyright&quot;>OpenStreetMap</a> contributors"
                        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                        crossOrigin="anonymous" // CORS tiles: real-size cache entries (no opaque-response quota padding)
                    />
                    <MapDraw onEdited={onEdited} editing={true} center={center} radius={radius} />
                </SMap>
            </Grid>

            <Button
                onClick={handleGeofenceSubmit}
                sx={{
                    margin: "20px 0px  0px 0px",
                    padding: "8px", fontSize: "14px", borderRadius: "5px",
                    color: theme.palette.background.paper,
                    backgroundColor: theme.palette.primary.main,
                    ":hover": {
                        color: theme.palette.background.paper,
                        backgroundColor: theme.palette.primary.main,
                    }
                }}>
                {translate('ra.action.confirm')}
            </Button>

        </Grid >
    )

}

const GeofenceInput = ({
    record,
    openDialog, setOpenDialog
}) => {

    return <StandardDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth={false}
        BodyComponent={() => <BodyComponent record={record} setOpenDialog={setOpenDialog} />}

    />
}




export default GeofenceInput
